import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterTygerberg() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-tygerberg",
    name: "Painters Tygerberg",
    location: "Tygerberg, Cape Town",
    rating: 4.8,
    reviews: 158,
    specialties: ["Hospital Properties", "Medical Facilities", "Residential Painting", "Commercial Projects"],
    image: "https://public.readdy.ai/ai/img_res/0966abb4ea7a18caeff984fa5a1411b6.jpg",
    verified: true,
    yearsExperience: 14,
    website: "https://painterstygerberg.co.za",
    email: "info@painterstygerberg.co.za",
    phone: "+27 21 555 0142",
    description: "Professional painting services in Tygerberg and surrounding Northern Suburbs. We specialize in residential properties, medical facilities, and commercial projects with expertise in high-quality finishes and comprehensive property maintenance.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Medical Facility Painting",
      "Commercial Property Painting",
      "Residential Estate Painting",
      "Waterproofing Services",
      "Roof Painting & Coatings",
      "Color Consultation & Design"
    ],
    serviceAreas: ["Tygerberg", "Parow", "Bellville", "Welgemoed", "Panorama", "Durbanville", "Northern Suburbs"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Tygerberg%20Cape%20Town%2C%20modern%20home%20with%20fresh%20white%20walls%20and%20mountain%20views%2C%20quality%20paint%20finish%2C%20Tygerberg%20Hills%20scenery%20visible%20through%20windows%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work%20with%20contemporary%20furniture&width=400&height=300&seq=tygerberg-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Tygerberg%20Cape%20Town%20South%20Africa%2C%20residential%20home%20with%20fresh%20paint%20overlooking%20Tygerberg%20Hills%2C%20professional%20painters%20working%20on%20scaffolding%2C%20quality%20exterior%20finish%2C%20suburban%20setting%20with%20mountain%20views%2C%20well-maintained%20property%20with%20beautiful%20landscaping&width=400&height=300&seq=tygerberg-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Commercial%20building%20painting%20project%20in%20Tygerberg%20medical%20district%2C%20professional%20painters%20working%20on%20medical%20facility%20exterior%2C%20quality%20commercial%20paint%20finish%2C%20modern%20healthcare%20architecture%2C%20organized%20work%20site%20with%20painting%20equipment%20and%20materials&width=400&height=300&seq=tygerberg-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Kitchen%20interior%20painting%20in%20Tygerberg%20residential%20home%20Cape%20Town%2C%20fresh%20painted%20walls%20in%20modern%20kitchen%2C%20quality%20paint%20finish%20with%20neutral%20color%20scheme%2C%20bright%20clean%20space%20with%20mountain%20views%2C%20professional%20painting%20work%20with%20contemporary%20fixtures%20and%20appliances&width=400&height=300&seq=tygerberg-gallery-4&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Bedroom%20interior%20painting%20in%20Tygerberg%20family%20home%2C%20fresh%20painted%20walls%20in%20spacious%20bedroom%2C%20modern%20design%20with%20Tygerberg%20Hills%20views%2C%20quality%20paint%20finish%2C%20bright%20airy%20space%2C%20professional%20painting%20work%20with%20elegant%20decor%20and%20natural%20lighting&width=400&height=300&seq=tygerberg-gallery-5&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20residential%20property%20painting%20in%20Tygerberg%20Cape%20Town%2C%20professional%20painters%20applying%20quality%20paint%20to%20suburban%20home%2C%20Tygerberg%20Hills%20backdrop%2C%20quality%20exterior%20finish%20with%20weather%20protection%2C%20organized%20professional%20work%20site%20with%20safety%20equipment&width=400&height=300&seq=tygerberg-gallery-6&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Dr. Johan Pretorius",
        rating: 5,
        comment: "Excellent work on our Tygerberg medical practice! The team was professional, efficient, and completed the project with minimal disruption to our operations. The quality of work is outstanding and the finish is perfect."
      },
      {
        name: "Linda Jacobs", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our Tygerberg home painting on schedule and the quality exceeded our expectations. Very reliable and the attention to detail was impressive."
      },
      {
        name: "Mark Williams",
        rating: 5,
        comment: "Great attention to detail and very experienced with both residential and commercial painting. Our property looks fantastic and we've received many compliments from neighbors and colleagues."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Tygerberg | Professional Residential & Commercial Painting Services Cape Town | Northern Suburbs Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Tygerberg offers professional painting services for residential properties, medical facilities, and commercial projects in Tygerberg, Cape Town. Specializing in high-quality finishes, property maintenance, and Northern Suburbs painting. Expert painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Tygerberg, house painters Tygerberg, commercial painting contractors Cape Town, medical facility painting, Northern Suburbs painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[70vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20stunning%20residential%20property%20in%20Tygerberg%20Cape%20Town%20South%20Africa%2C%20spectacular%20Tygerberg%20Hills%20views%20with%20medical%20district%2C%20painters%20in%20clean%20uniforms%20applying%20paint%20with%20brushes%20and%20rollers%2C%20modern%20suburban%20architecture%2C%20bright%20sunny%20day%2C%20pristine%20work%20environment%20with%20professional%20equipment%20and%20organized%20work%20site&width=1200&height=600&seq=tygerberg-painters-hero-bg&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-lime-400 text-black px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-shield-check-line mr-2"></i>
              VERIFIED PROFESSIONAL PAINTER
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Painters Tygerberg
            </h1>
            <p className="text-2xl text-gray-300 mb-4">
              Expert Residential & Commercial Painting Services
            </p>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Tygerberg's most trusted painting contractors specializing in residential properties, medical facilities, and commercial projects. With over 14 years of experience serving the Northern Suburbs, we deliver exceptional quality and reliability using premium materials and proven techniques designed to enhance and protect properties in this thriving community.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Navigation Tabs */}
              <nav className="flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'services', label: 'Services' },
                  { id: 'gallery', label: 'Gallery' },
                  { id: 'reviews', label: 'Reviews' },
                  { id: 'contact', label: 'Contact' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                      activeTab === tab.id
                        ? 'border-lime-500 text-lime-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              {/* Tab Content */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                {activeTab === 'overview' && (
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Tygerberg</h2>
                      <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
                        <p className="leading-relaxed">
                          Welcome to Painters Tygerberg, the Northern Suburbs' premier painting contractor for residential properties, medical facilities, and commercial projects. For over 14 years, we have been the trusted choice for homeowners, healthcare providers, and business owners throughout Tygerberg, Parow, Bellville, and the surrounding Northern Suburbs who demand superior craftsmanship, reliability, and professional service for their properties.
                        </p>
                        <p className="leading-relaxed">
                          Our reputation has been built on a solid foundation of exceptional quality workmanship, meticulous attention to detail, and an unwavering commitment to customer satisfaction. We understand that maintaining properties in the Tygerberg area requires specialized knowledge and expertise, particularly when working with medical facilities and commercial properties that have specific requirements and standards. We have developed comprehensive expertise in delivering painting services that meet these exacting standards while providing excellent value and minimal disruption to operations.
                        </p>
                        <p className="leading-relaxed">
                          What truly distinguishes Painters Tygerberg from other painting contractors is our versatile expertise across residential, commercial, and specialized medical facility painting. The combination of diverse property types, varying requirements, strict health and safety standards, and the need for efficient project completion requires specialized knowledge, advanced techniques, and proven project management capabilities. Our team has spent years perfecting our methods to ensure that every project we complete not only looks outstanding but also meets all relevant standards and provides maximum durability and protection.
                        </p>
                        <p className="leading-relaxed">
                          We take immense pride in our comprehensive, detail-oriented approach to every painting project. From the moment you contact us for an initial consultation through to the final walk-through inspection, we maintain the highest professional standards at every stage. Our process begins with a thorough assessment of your property's condition, taking into account factors such as existing paint condition, substrate type, usage requirements, specific regulations or standards that apply, and any particular concerns you may have. We then provide detailed recommendations and a transparent, itemized quote with no hidden costs or surprises.
                        </p>
                        <p className="leading-relaxed">
                          Our team of skilled painters are not simply workers applying paint to surfaces; they are experienced craftsmen who take genuine pride in their work and understand the importance of delivering flawless results that meet professional standards. Each team member has been carefully selected for their skills, reliability, professionalism, and commitment to excellence. We invest heavily in ongoing training to ensure our team stays current with the latest techniques, products, health and safety requirements, and industry best practices, including specialized training in medical facility painting and commercial property maintenance.
                        </p>
                        <p className="leading-relaxed">
                          Over the years, we have built an impressive portfolio of completed projects throughout the Tygerberg area and Northern Suburbs. From family homes and residential estates to medical practices, clinics, commercial offices, and retail spaces, we have successfully completed hundreds of projects of varying sizes and complexity. Each project receives the same level of dedication, professionalism, and attention to detail, regardless of its scope. We treat every property as if it were our own, ensuring that the final result exceeds your expectations and meets all relevant standards and requirements.
                        </p>
                        <p className="leading-relaxed">
                          At Painters Tygerberg, we believe in building long-term relationships with our clients based on trust, transparency, and consistently excellent results. Many of our customers have been with us for years, returning for maintenance work, renovations, and new projects. They recommend us to their friends, family, colleagues, and business associates because they know they can rely on us to deliver superior quality, honest advice, fair pricing, and professional service every single time. This loyalty and trust is something we never take for granted, and it drives us to maintain our high standards on every project.
                        </p>
                        <p className="leading-relaxed">
                          Our commitment to excellence extends far beyond the quality of our painting work. We understand that having contractors working in and around your property requires a high level of trust, professionalism, and consideration for your needs. That's why we maintain clean, organized work sites, protect your furniture and belongings, respect your property and privacy, work efficiently to minimize disruption to your daily routine or business operations, and ensure our team members conduct themselves with the professionalism expected in all environments, particularly medical and commercial settings. We communicate clearly and regularly throughout the project, keeping you informed of progress and addressing any questions or concerns promptly.
                        </p>
                        <p className="leading-relaxed">
                          We use only premium, environmentally responsible paints and materials from leading manufacturers such as Plascon, Dulux, and specialized coating suppliers. These products are specifically formulated to provide superior adhesion, durability, aesthetic appeal, and compliance with relevant health and safety standards. By investing in the best materials available, we ensure that your paint job not only looks stunning on completion day but continues to look great and provide protection for many years to come, maintaining the professional appearance and functionality required for residential, medical, and commercial properties.
                        </p>
                      </div>
                    </div>

                    <div className="border-t pt-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Painters Tygerberg?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-award-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">14+ Years Experience</h4>
                            <p className="text-gray-600 text-sm">Over a decade of proven excellence in residential, commercial, and medical facility painting throughout the Northern Suburbs.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-shield-check-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Fully Insured & Certified</h4>
                            <p className="text-gray-600 text-sm">Comprehensive liability insurance and professional certifications for your complete peace of mind and protection.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-star-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Versatile Expertise</h4>
                            <p className="text-gray-600 text-sm">Specialized knowledge in residential, commercial, and medical facility painting with proven track record.</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-lime-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-customer-service-line text-lime-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Professional Service</h4>
                            <p className="text-gray-600 text-sm">Clear communication, respect for your property, and commitment to exceeding expectations in all projects.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Service Areas</h3>
                      <p className="text-gray-700 mb-4 leading-relaxed">
                        Painters Tygerberg proudly serves the Northern Suburbs and surrounding areas, including:
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {['Tygerberg', 'Parow', 'Bellville', 'Welgemoed', 'Panorama', 'Durbanville', 'Goodwood', 'Monte Vista', 'Plattekloof'].map((area) => (
                          <div key={area} className="flex items-center space-x-2 text-gray-700">
                            <i className="ri-map-pin-line text-lime-600"></i>
                            <span>{area}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'services' && (
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Comprehensive Painting Services</h2>
                      <p className="text-gray-700 mb-8 leading-relaxed">
                        At Painters Tygerberg, we offer a complete range of professional painting services designed for residential properties, medical facilities, and commercial projects. Our expertise encompasses every aspect of professional painting, from initial consultation and color selection through to meticulous surface preparation, expert application, and thorough final inspection.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-home-4-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Residential Painting Excellence</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Transform your living spaces with our premium residential painting services. We specialize in creating beautiful, durable finishes that enhance your home's aesthetic appeal and value. Our meticulous surface preparation and expert application techniques ensure flawless results in every room.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Living rooms, bedrooms, and family areas</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Kitchens and bathrooms with moisture-resistant coatings</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Ceilings, cornices, and architectural features</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Doors, window frames, and trim work</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-hospital-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Medical Facility Painting</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Specialized painting services for medical practices, clinics, and healthcare facilities. We understand the unique requirements of medical environments and use appropriate materials and techniques that meet health and safety standards while minimizing disruption to operations.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Medical practices and dental clinics</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Healthcare facilities and treatment rooms</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Waiting areas and reception spaces</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Compliance with health and safety standards</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-building-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Commercial Property Painting</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Professional painting services for commercial properties including offices, retail spaces, and business facilities. We work efficiently to minimize disruption to your business operations while delivering high-quality results that enhance your professional image.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Office buildings and corporate spaces</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Retail stores and shopping centers</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Industrial facilities and warehouses</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Flexible scheduling to minimize disruption</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="w-14 h-14 bg-lime-400 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-paint-brush-line text-black text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Specialty Services</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">
                          Comprehensive range of specialty painting and coating services to protect and enhance your property. From waterproofing to roof coatings and decorative finishes, we have the expertise to handle all your painting needs.
                        </p>
                        <ul className="space-y-2 text-gray-600 text-sm">
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Waterproofing and damp proofing solutions</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Roof painting and protective coatings</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Textured and decorative finishes</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-lime-600 mr-2 mt-1"></i>
                            <span>Color consultation and design services</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'gallery' && (
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {painter.gallery.map((image, index) => (
                        <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                          <img 
                            src={image} 
                            alt={`${painter.name} project ${index + 1}`}
                            className="w-full h-64 object-cover object-top"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
                    <div className="space-y-6">
                      {painter.testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                          <div className="flex items-center mb-4">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                              ))}
                            </div>
                            <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                          </div>
                          <p className="text-gray-700">{testimonial.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'contact' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                      <div className="space-y-6">
                        <div className="flex items-center">
                          <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Email</h3>
                            <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                              {painter.email}
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Website</h3>
                            <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                              {painter.website}
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                          <div>
                            <h3 className="font-semibold text-gray-900">Service Area</h3>
                            <p className="text-gray-700">{painter.location} and surrounding areas</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                      <button 
                        onClick={() => navigate('/quote')}
                        className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                      >
                        Request Free Quote
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                <div className="mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20property%20in%20Tygerberg%20Cape%20Town%2C%20Tygerberg%20Hills%20and%20medical%20district%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20modern%20suburban%20architecture%2C%20bright%20natural%20lighting&width=400&height=300&seq=tygerberg-sidebar-image&orientation=landscape"
                    alt="Professional painters Tygerberg residential commercial medical facility painting services Cape Town Northern Suburbs"
                    className="w-full h-48 object-cover object-top rounded-lg"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-mail-line mr-3 text-lime-500"></i>
                    <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                      {painter.email}
                    </a>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-global-line mr-3 text-lime-500"></i>
                    <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                      Visit Website
                    </a>
                  </div>
                </div>
                
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get Free Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
